import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Building2,
  Users,
  Shield,
  FileText,
  FilePlus,
  FileCheck,
  AlertTriangle,
  CreditCard,
  Receipt,
  Palette,
  Scale,
  BookOpen,
  MessageSquare,
  Sparkles,
} from 'lucide-react';
import { NewsUnreadBadge } from '@/features/news/components/reader/NewsUnreadBadge';
import { NEWS_FEED_PATH } from '@/features/news/constants/news.constants';
import { useAuthStore } from '../stores/auth-store';
import { useStripeAdminAccounts } from '../features/stripe-admin/hooks/useStripeAdminAccounts';
import { useSocietyChildren } from '../features/societies/hooks/useSocieties';
import { useEffectiveConfiguration } from '../features/societies/hooks/useSocietyConfiguration';
import { useAvailableSocieties } from '../hooks/societies/useAvailableSocieties';
import { useCurrentUserWording } from '../hooks/useVendorWording';
import { savApi } from '../features/sav/api/savApi';
import { repairerInvoiceApi } from '../features/sav/api/repairerInvoiceApi';

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  permission?: string;
  /** Si fourni, l'item est visible si l'utilisateur a AU MOINS UNE de ces permissions */
  permissions?: string[];
  disabled?: boolean;
  requiredSocietyOption?: 'canCreateContract' | 'deliveryProofCreate' | 'presaleSheet' | 'paymentCycle';
  /** Visible uniquement si billedId est null ou billedId === societyId de l'utilisateur */
  requiresBilledAccess?: boolean;
  /** Visible uniquement si l'utilisateur possède ≥1 compte Stripe supervisable (serveur) */
  requiresStripeAccess?: boolean;
  badge?: number;
  /** Second badge distinct (messages garant non lus) — pastille bleue à icône. */
  messageBadge?: number;
  /** Entrée « Nouveautés » : pastille du nombre de nouveautés non lues (rien à 0). */
  newsBadge?: boolean;
  /** Reste active sur les sous-pages (par défaut, seule la correspondance exacte l'est). */
  matchSubPaths?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
  highlighted?: boolean;
}

/**
 * Sidebar de navigation pour outil-pro (EXTERNAL uniquement)
 * Affiche les liens principaux et gère la navigation
 * Les onglets sont filtrés selon les permissions de l'utilisateur connecté
 * Menu organisé en sections (Données, Générer, Consulter, Services, Administration)
 */
interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
  const { hasPermission, currentUser, isAuthenticated } = useAuthStore();
  const wording = useCurrentUserWording();

  // Récupérer les enfants de la société de l'utilisateur connecté
  const { data: childrenData } = useSocietyChildren(
    currentUser?.societyId || '',
    { enabled: isAuthenticated },
  );

  // Récupérer la configuration effective de la société
  const { data: effectiveConfig } = useEffectiveConfiguration(
    currentUser?.societyId || undefined,
    { enabled: isAuthenticated },
  );

  // Logique permissive multi-société : au moins une société assignée doit
  // activer l'option concernée pour que le lien soit visible.
  const { societies: contractEligible } = useAvailableSocieties('contract');
  const { societies: deliveryProofEligible } = useAvailableSocieties('deliveryProof');
  const { societies: presaleSheetEligible } = useAvailableSocieties('presaleSheet');

  const hasChildren = useMemo(() => {
    return (childrenData || []).length > 0;
  }, [childrenData]);

  // Compter les SAV en attente de signature garant
  const canSign = hasPermission('sav.sign');
  const { data: savActionCount } = useQuery({
    queryKey: ['sav-action-required-count'],
    queryFn: () => savApi.getActionRequiredCount('garant'),
    enabled: isAuthenticated && canSign,
    refetchInterval: 60_000,
  });

  // Compter les messages garant non lus (badge « Mes SAV »). Visible à qui peut
  // ouvrir un SAV et voir les messages (sav.view / sav.view_all / sav.manage /
  // sav.sign) — jamais aux users sav.list-only, qui ne peuvent pas entrer dans
  // un dossier (aligné backend @RequirePermission).
  const canReadSavDetail =
    hasPermission('sav.view') ||
    hasPermission('sav.view_all') ||
    hasPermission('sav.manage') ||
    hasPermission('sav.sign');
  const { data: garantUnreadMessages } = useQuery({
    queryKey: ['garant-unread-messages-count'],
    queryFn: () => savApi.countUnreadGarantMessages(),
    // Garants EXTERNAL uniquement : un INTERNAL reçoit toujours 0 côté backend
    // (pas de « non-lu garant ») → inutile de le poller.
    enabled: isAuthenticated && canReadSavDetail && currentUser?.type === 'EXTERNAL',
    refetchInterval: 60_000,
  });

  // Compter les factures réparateur à régler (cycle paiement).
  // Double condition (anti-régression) :
  //  - permission : signer OU gérer les SAV (aligné backend
  //    @RequirePermission('sav.sign','sav.manage'))
  //  - cycle activé chez le garant (effectiveConfig.paymentCycleEnabled)
  // Sans cycle → query désactivée (aucun polling) ET menu masqué → ZÉRO impact
  // pour les ~99% de vendeurs dont le garant n'a pas le cycle.
  const canManageSav = hasPermission('sav.manage');
  const canSeeRepairerPayments =
    !!effectiveConfig?.paymentCycleEnabled && (canSign || canManageSav);
  const { data: repairerInvoicesToPay } = useQuery({
    queryKey: ['repairer-invoices-to-pay-count'],
    queryFn: () => repairerInvoiceApi.countToPay(),
    enabled: isAuthenticated && canSeeRepairerPayments,
    refetchInterval: 60_000,
  });

  // Comptes Stripe supervisables (résolu serveur) : masque l'onglet Stripe quand
  // l'utilisateur ne possède aucun compte (ex : agence en facturation centralisée).
  const { data: stripeAccounts } = useStripeAdminAccounts(
    isAuthenticated && hasPermission('stripe.self'),
  );

  // Vérifie si un item est visible selon les permissions et conditions
  const isItemVisible = (item: NavItem): boolean => {
    if (item.permission && !hasPermission(item.permission)) return false;
    if (item.permissions && !item.permissions.some(p => hasPermission(p))) return false;

    // Condition spéciale pour l'onglet Sociétés : masquer si utilisateur sans enfants
    if (item.permission === 'society.list') {
      return hasChildren;
    }

    // Vérifier l'option société si requise (logique permissive multi-société)
    if (item.requiredSocietyOption) {
      if (item.requiredSocietyOption === 'canCreateContract') {
        return contractEligible.length > 0;
      }
      if (item.requiredSocietyOption === 'deliveryProofCreate') {
        return deliveryProofEligible.length > 0;
      }
      if (item.requiredSocietyOption === 'presaleSheet') {
        return presaleSheetEligible.length > 0;
      }
      // Cycle paiement réparateur : visible seulement si le garant de la
      // société a activé le cycle (aucune apparition pour les vendeurs OFF).
      if (item.requiredSocietyOption === 'paymentCycle') {
        return !!effectiveConfig?.paymentCycleEnabled;
      }
    }

    // Vérifier l'accès facturation (billedId)
    if (item.requiresBilledAccess) {
      const billedId = effectiveConfig?.billedId;
      if (billedId && billedId !== currentUser?.societyId) {
        return false;
      }
    }

    // Vérifier l'accès Stripe : ≥1 compte supervisable (source de vérité serveur)
    if (item.requiresStripeAccess) {
      return (stripeAccounts?.length ?? 0) > 0;
    }

    return true;
  };

  const sections: NavSection[] = [
    {
      label: 'Données',
      items: [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Tableau de bord', permissions: [
          'dashboard.view_contracts', 'dashboard.view_revenue',
          'dashboard.view_sav_claims', 'dashboard.view_sav_amounts',
          'dashboard.view_provision', 'dashboard.view_encaissements', 'dashboard.view_synthese',
          'dashboard.view_upsell',
        ] },
      ],
    },
    {
      label: 'Générer',
      highlighted: true,
      items: [
        { to: '/contracts/create', icon: FilePlus, label: 'Un contrat client', permission: 'contract.create', requiredSocietyOption: 'canCreateContract' },
        { to: '/delivery-proofs/new', icon: FileCheck, label: 'Une attestation de livraison', permission: 'delivery_proof.create', requiredSocietyOption: 'deliveryProofCreate' },
      ],
    },
    {
      label: 'Consulter',
      items: [
        { to: '/contracts', icon: FileText, label: 'Mes contrats client', permission: 'contract.list' },
        { to: '/delivery-proofs', icon: FileCheck, label: 'Mes attestations de livraison', permission: 'delivery_proof.list', requiredSocietyOption: 'deliveryProofCreate' },
        { to: '/warranties', icon: Shield, label: `Ma stratégie de ${wording.plural.toLowerCase()}`, permission: 'warranty.list' },
        { to: '/sav', icon: AlertTriangle, label: 'Mes SAV', permission: 'sav.list', badge: savActionCount || 0, messageBadge: garantUnreadMessages || 0 },
        { to: '/sav/repairer-invoices', icon: CreditCard, label: 'Factures réparateur', permissions: ['sav.sign', 'sav.manage'], requiredSocietyOption: 'paymentCycle', badge: repairerInvoicesToPay || 0 },
        { to: '/invoices', icon: Receipt, label: 'Mes factures', permission: 'invoice.list', requiresBilledAccess: true },
        { to: '/online-payments', icon: CreditCard, label: 'Mes paiements en ligne', permission: 'stripe.self', requiresStripeAccess: true },
      ],
    },
    {
      label: 'Services',
      items: [
        { to: NEWS_FEED_PATH, icon: Sparkles, label: 'Nouveautés', newsBadge: true, matchSubPaths: true },
        { to: '/presale-sheet', icon: BookOpen, label: 'Brochure avant vente', requiredSocietyOption: 'presaleSheet' },
        { to: '/plv', icon: Palette, label: 'PLV Personnalisée', permission: 'plv.list', requiresBilledAccess: true },
        { to: '/legal-library', icon: Scale, label: 'Bibliothèque juridique' },
      ],
    },
    {
      label: 'Administration',
      items: [
        { to: '/societies', icon: Building2, label: 'Mes points de vente', permission: 'society.list' },
        { to: '/users-externes', icon: Users, label: 'Mes utilisateurs', permission: 'external_user.list' },
      ],
    },
  ];

  const renderNavItem = (item: NavItem, highlighted?: boolean) => {
    const Icon = item.icon;

    if (item.disabled) {
      return (
        <div
          key={item.to}
          className="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-text-gray-600 tw-rounded-lg tw-cursor-not-allowed"
          title="Fonctionnalité à venir"
        >
          <Icon className="tw-w-5 tw-h-5" />
          <span className="tw-text-sm tw-font-medium">{item.label}</span>
          <span className="tw-ml-auto tw-text-xs tw-bg-gray-800 tw-text-gray-400 tw-px-2 tw-py-1 tw-rounded">
            Bientôt
          </span>
        </div>
      );
    }

    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={!item.matchSubPaths}
        onClick={onClose}
        className={({ isActive }) =>
          highlighted
            ? `sidebar-generate-link${isActive ? ' active' : ''}`
            : `tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-rounded-lg tw-transition-colors tw-no-underline ${
                isActive ? 'tw-bg-affinicar-secondary !tw-text-white' : 'tw-text-gray-300 hover:!tw-text-white'
              }`
        }
      >
        {({ isActive }) => (
          <>
            {!highlighted && <Icon className={`tw-w-5 tw-h-5 ${isActive ? 'tw-text-white' : ''}`} />}
            {highlighted && <Icon />}
            <span className="tw-text-sm tw-font-medium">{item.label}</span>
            {item.newsBadge && <NewsUnreadBadge platform="PRO" className="tw-ml-auto" />}
            {(item.badge && item.badge > 0) ||
            (item.messageBadge && item.messageBadge > 0) ? (
              <span className="tw-ml-auto tw-flex tw-items-center tw-gap-1">
                {item.badge && item.badge > 0 ? (
                  <span className="tw-bg-red-500 tw-text-white tw-text-xs tw-font-bold tw-min-w-[20px] tw-h-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-px-1.5">
                    {item.badge}
                  </span>
                ) : null}
                {item.messageBadge && item.messageBadge > 0 ? (
                  <span
                    title="Messages non lus"
                    className="tw-bg-blue-500 tw-text-white tw-text-xs tw-font-bold tw-min-w-[20px] tw-h-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-px-1.5 tw-gap-0.5"
                  >
                    <MessageSquare className="tw-w-3 tw-h-3" />
                    {item.messageBadge}
                  </span>
                ) : null}
              </span>
            ) : null}
          </>
        )}
      </NavLink>
    );
  };

  const visibleSections = sections
    .map(section => ({
      ...section,
      items: section.items.filter(isItemVisible),
    }))
    .filter(section => section.items.length > 0);

  return (
    <>
      {/* Overlay mobile */}
      {isOpen && (
        <div
          className="tw-fixed tw-inset-0 tw-bg-black/50 tw-z-40 tw-mt-16 md:tw-hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          tw-w-64 tw-bg-affinicar-dark tw-flex tw-flex-col tw-pt-16 tw-h-full
          tw-fixed tw-left-0 tw-top-0 tw-z-50
          tw-transition-transform tw-duration-300 tw-ease-in-out
          ${isOpen ? 'tw-translate-x-0' : 'tw--translate-x-full'}
          md:tw-translate-x-0 md:tw-z-30
        `}
      >
        {/* Navigation */}
        <nav className="tw-flex-1 tw-px-3 tw-py-2 tw-overflow-y-auto">
          {visibleSections.map((section, index) => (
            <div key={section.label}>
              {index > 0 && (
                <div className="tw-border-t tw-border-gray-700/30 tw-mx-2 tw-my-1.5" />
              )}
              <div className={section.highlighted ? 'sidebar-section-generate' : ''}>
                <div className={section.highlighted ? 'sidebar-section-generate__label' : 'tw-px-3 tw-mb-1 tw-mt-1'}>
                  <span className={`tw-text-[10px] tw-uppercase tw-tracking-widest tw-font-semibold ${section.highlighted ? '' : 'tw-text-gray-500'}`}>
                    {section.label}
                  </span>
                </div>
                <div>
                  {section.items.map(item => renderNavItem(item, section.highlighted))}
                </div>
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};
